import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { api } from 'src/api/config';
import {
  Box,
  Typography,
  Paper,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  Switch,
  FormControlLabel,
  Button,
  CircularProgress,
  Divider,
  Grid,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { getToken } from 'src/auth';

/**
 * Settings Tab Component
 * Manage ISO settings including fees, permissions, and POS integration
 */
function SettingsTabComponent({ financialInstitutions, onSaveSettings, token }) {
  const [selectedFiId, setSelectedFiId] = useState('');
  const [fiData, setFiData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // Filter to only show payment-enabled FIs
  const paymentEnabledFIs = (financialInstitutions || []).filter(
    fi => fi.financial_institution_allows_payments
  );

  // Fetch FI details when selected
  // Fetches from both /api/v1/fis/ (general FI data) and /api/v1/pos/fis/ (commission data)
  useEffect(() => {
    if (!selectedFiId) {
      setFiData(null);
      return;
    }

    const fetchFIDetails = async () => {
      setLoading(true);
      setError(null);
      try {
        // Fetch general FI data
        const fiResponse = await fetch(`${api.hostname}/api/v1/fis/${selectedFiId}/`, {
          headers: {
            'Authorization': `Bearer ${token.accessToken}`,
          },
        });

        if (!fiResponse.ok) throw new Error('Failed to fetch FI details');
        const fiData = await fiResponse.json();

        // Fetch commission data from POS proxy (source of truth for commission)
        const posResponse = await fetch(`${api.hostname}/api/v1/pos/fis/${selectedFiId}/`, {
          headers: {
            'Authorization': `Bearer ${token.accessToken}`,
          },
        });

        let commissionData = {};
        if (posResponse.ok) {
          const posData = await posResponse.json();
          // Map POS field names to component's expected field names
          commissionData = {
            financial_institution_reseller_split_percentage: posData.reseller_split_percentage,
            financial_institution_buy_rate_percentage: posData.buy_rate_percentage,
            financial_institution_buy_rate_usd: posData.buy_rate_usd,
            financial_institution_reseller_split_usd: posData.reseller_split_usd,
          };
        }

        // Merge FI data with commission data
        const mergedData = { ...fiData, ...commissionData };
        console.log('Loaded FI data (merged):', mergedData);
        setFiData(mergedData);
        setHasChanges(false);
      } catch (err) {
        console.error('Failed to load FI:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchFIDetails();
  }, [selectedFiId, token]);

  const handleFieldChange = (field, value) => {
    setFiData(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
    setSuccess(false);
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(false);

    try {
      // Commission fields that go to POS proxy
      const commissionFields = [
        'financial_institution_reseller_split_percentage',
        'financial_institution_buy_rate_percentage',
        'financial_institution_buy_rate_usd',
        'financial_institution_reseller_split_usd',
      ];

      // Fields to exclude from general FI update (file uploads, foreign keys, read-only, commission)
      const excludeFields = [
        'id',
        'financial_institution_logo',
        'financial_institution_logo_small',
        'financial_institution_logo_blue',
        'financial_institution_logo_email',
        'financial_institution_type',
        'created_date',
        'updated_date',
        'questions',
        'document_types',
        'consent_types',
        'industries',
        'activities',
        'services',
        ...commissionFields, // Commission fields go to POS, not TerraBE
      ];

      // Convert to FormData for multipart/form-data encoding (general FI data)
      const formData = new FormData();

      // Add only editable non-commission fields to FormData
      Object.keys(fiData).forEach(key => {
        // Skip excluded fields
        if (excludeFields.includes(key)) {
          return;
        }

        const value = fiData[key];
        // Include null/undefined/empty string - backend expects these for optional fields
        if (value !== null && value !== undefined) {
          formData.append(key, value === '' ? '' : value);
        }
      });

      // Save general FI data to TerraBE
      const fiResponse = await fetch(`${api.hostname}/api/v1/fis/${selectedFiId}/`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token.accessToken}`,
          // Don't set Content-Type - browser will set it with boundary
        },
        body: formData,
      });

      if (!fiResponse.ok) {
        const errorData = await fiResponse.json().catch(() => ({}));
        const errorMessage = errorData.detail || JSON.stringify(errorData) || 'Failed to save FI settings';
        throw new Error(errorMessage);
      }

      // Save commission data to POS proxy (source of truth)
      const posPayload = {
        allows_payments: fiData.financial_institution_allows_payments,
        reseller_split_percentage: fiData.financial_institution_reseller_split_percentage,
        buy_rate_percentage: fiData.financial_institution_buy_rate_percentage,
        reseller_split_usd: fiData.financial_institution_reseller_split_usd || '0.00',
        buy_rate_usd: fiData.financial_institution_buy_rate_usd || '0.00',
      };

      console.log('Saving commission data to POS:', posPayload);

      const posResponse = await fetch(`${api.hostname}/api/v1/pos/fis/${selectedFiId}/`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token.accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(posPayload),
      });

      if (!posResponse.ok) {
        const errorData = await posResponse.json().catch(() => ({}));
        console.error('Failed to save commission data:', errorData);
        // Don't throw - FI data was saved, just warn about commission
        setError('FI settings saved, but commission update failed: ' + (errorData.error || 'Unknown error'));
      }

      // Refetch merged data to update state
      const updatedFiResponse = await fetch(`${api.hostname}/api/v1/fis/${selectedFiId}/`, {
        headers: { 'Authorization': `Bearer ${token.accessToken}` },
      });
      const updatedFiData = await updatedFiResponse.json();

      const updatedPosResponse = await fetch(`${api.hostname}/api/v1/pos/fis/${selectedFiId}/`, {
        headers: { 'Authorization': `Bearer ${token.accessToken}` },
      });
      let updatedCommissionData = {};
      if (updatedPosResponse.ok) {
        const posData = await updatedPosResponse.json();
        updatedCommissionData = {
          financial_institution_reseller_split_percentage: posData.reseller_split_percentage,
          financial_institution_buy_rate_percentage: posData.buy_rate_percentage,
          financial_institution_buy_rate_usd: posData.buy_rate_usd,
          financial_institution_reseller_split_usd: posData.reseller_split_usd,
        };
      }

      const mergedData = { ...updatedFiData, ...updatedCommissionData };
      setFiData(mergedData);
      setSuccess(true);
      setHasChanges(false);

      // Notify parent if callback provided
      if (onSaveSettings) {
        onSaveSettings(mergedData);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    // Reset by refetching original data
    if (selectedFiId) {
      setSelectedFiId('');
      setTimeout(() => setSelectedFiId(selectedFiId), 0);
    }
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
        ISO Settings
      </Typography>

      {/* FI Selector */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <FormControl fullWidth>
          <InputLabel>Select ISO</InputLabel>
          <Select
            value={selectedFiId}
            onChange={(e) => setSelectedFiId(e.target.value)}
            label="Select ISO"
          >
            <MenuItem value="">
              <em>Choose an ISO...</em>
            </MenuItem>
            {paymentEnabledFIs.map(fi => (
              <MenuItem key={fi.id} value={fi.id}>
                {fi.financial_institution_business_name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Paper>

      {/* Loading State */}
      {loading && (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      )}

      {/* Error State */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Success State */}
      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(false)}>
          Settings saved successfully!
        </Alert>
      )}

      {/* Settings Form */}
      {!loading && fiData && (
        <>
          {/* Business Information */}
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">
                Business Information
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Business Name"
                    value={fiData.financial_institution_business_name ?? ''}
                    onChange={(e) => handleFieldChange('financial_institution_business_name', e.target.value)}
                    helperText="Official legal business name"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="DBA Name (Optional)"
                    value={fiData.financial_institution_dba_name ?? ''}
                    onChange={(e) => handleFieldChange('financial_institution_dba_name', e.target.value)}
                    helperText="Doing Business As name"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Support Contact Name"
                    value={fiData.financial_institution_support_name ?? ''}
                    onChange={(e) => handleFieldChange('financial_institution_support_name', e.target.value)}
                    helperText="Primary support contact name"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Support Email (Optional)"
                    type="email"
                    placeholder="support@example.com"
                    value={fiData.financial_institution_support_email ?? ''}
                    onChange={(e) => handleFieldChange('financial_institution_support_email', e.target.value)}
                    helperText="Support contact email"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Support Phone"
                    type="tel"
                    placeholder="+1 (555) 123-4567"
                    value={fiData.financial_institution_support_phone ?? ''}
                    onChange={(e) => handleFieldChange('financial_institution_support_phone', e.target.value)}
                    helperText="Enter phone in any format"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Website"
                    type="url"
                    placeholder="https://example.com"
                    value={fiData.financial_institution_website ?? ''}
                    onChange={(e) => handleFieldChange('financial_institution_website', e.target.value)}
                    helperText="Optional website URL"
                  />
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          {/* Fees & Commission */}
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">
                Fees & Commission (Share-of-Net Revenue Model)
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    <strong>Revenue Split Model:</strong> Net revenue (after platform costs) is split between the ISO and Solvent.
                  </Alert>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Reseller Split Percentage"
                    type="number"
                    inputProps={{ min: 0, max: 100, step: 0.01 }}
                    value={parseFloat(fiData.financial_institution_reseller_split_percentage) || 60}
                    onChange={(e) => {
                      const value = e.target.value;
                      handleFieldChange('financial_institution_reseller_split_percentage', value);
                    }}
                    InputProps={{
                      endAdornment: '%',
                    }}
                    helperText="Percentage of net revenue allocated to ISO (0-100%)"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Buy Rate / Minimum Fee"
                    type="number"
                    inputProps={{ min: 0, max: 100, step: 0.01 }}
                    value={parseFloat(fiData.financial_institution_buy_rate_percentage) || 1}
                    onChange={(e) => {
                      const value = e.target.value;
                      handleFieldChange('financial_institution_buy_rate_percentage', value);
                    }}
                    InputProps={{
                      endAdornment: '%',
                    }}
                    helperText="Solvent's guaranteed minimum (buy rate) — ISO earns a split of fees above this"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="body2" color="text.secondary">
                    <strong>Example:</strong> For a $100 transaction with 3% merchant fee, if net revenue is $2 after costs:
                    <ul>
                      <li>ISO receives: $2 × {fiData.financial_institution_reseller_split_percentage || 60}% = ${((2 * (fiData.financial_institution_reseller_split_percentage || 60)) / 100).toFixed(2)}</li>
                      <li>Solvent receives: $2 × {100 - (fiData.financial_institution_reseller_split_percentage || 60)}% = ${((2 * (100 - (fiData.financial_institution_reseller_split_percentage || 60))) / 100).toFixed(2)}</li>
                    </ul>
                  </Typography>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          {/* POS Integration */}
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">
                POS Integration Settings
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <FormControlLabel
                control={
                  <Switch
                    checked={fiData.financial_institution_allows_payments || false}
                    onChange={(e) => handleFieldChange('financial_institution_allows_payments', e.target.checked)}
                  />
                }
                label="Enable Crypto Payments"
              />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                When enabled, this ISO can offer crypto payment processing to their merchant accounts.
              </Typography>
            </AccordionDetails>
          </Accordion>

          {/* Status & Permissions */}
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">
                Status & Permissions
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={fiData.financial_institution_allows_banking || false}
                        onChange={(e) => handleFieldChange('financial_institution_allows_banking', e.target.checked)}
                      />
                    }
                    label="Allow Banking Services"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={fiData.financial_institution_allows_lending || false}
                        onChange={(e) => handleFieldChange('financial_institution_allows_lending', e.target.checked)}
                      />
                    }
                    label="Allow Lending Services"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={fiData.financial_institution_allows_insurance || false}
                        onChange={(e) => handleFieldChange('financial_institution_allows_insurance', e.target.checked)}
                      />
                    }
                    label="Allow Insurance Services"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={fiData.financial_institution_is_archived || false}
                        onChange={(e) => handleFieldChange('financial_institution_is_archived', e.target.checked)}
                        color="error"
                      />
                    }
                    label="Archived (Hidden)"
                  />
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          {/* Save/Reset Actions */}
          <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              onClick={handleReset}
              disabled={!hasChanges || saving}
            >
              Reset Changes
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={!hasChanges || saving}
              startIcon={saving && <CircularProgress size={20} />}
            >
              {saving ? 'Saving...' : 'Save Settings'}
            </Button>
          </Box>
        </>
      )}

      {/* Empty State */}
      {!loading && !fiData && !error && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            Select an ISO to manage settings
          </Typography>
        </Paper>
      )}
    </Box>
  );
}

// Connect to Redux store
const mapStateToProps = (state) => ({
  token: getToken(state),
});

export const SettingsTab = connect(mapStateToProps)(SettingsTabComponent);
